import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaSpinner, FaClock, FaBook } from "react-icons/fa";
import { API_URL } from "../config/apiConfig";
import axios from "axios";

const HowToUse = () => {
  const navigate = useNavigate();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGuide, setSelectedGuide] = useState(null);

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/content/public/how-to-use`);
        if (response.data.success) {
          setGuides(response.data.data || []);
          if (response.data.data && response.data.data.length > 0) {
            setSelectedGuide(response.data.data[0]);
          }
        } else {
          setError("Failed to load guides");
        }
      } catch (err) {
        console.error("Error fetching guides:", err);
        setError("Unable to load guides. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchGuides();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-primary text-4xl mx-auto mb-4" />
          <p className="text-gray-600">Loading guides...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 md:py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Back Button */}
        <div className="mb-3 md:mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-primary hover:text-primary-dark transition-colors font-medium group"
          >
            <FaArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" />
            Go Back
          </button>
        </div>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-5 md:p-8 mb-4 md:mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-4">
            How to Use CabZigo
          </h1>
          <p className="text-gray-600 text-base md:text-lg">
            Step-by-step guides to help you get the most out of CabZigo
          </p>
        </div>

        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-700">{error}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Guides List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-4 md:p-6 border-b border-gray-200 bg-gray-50">
                  <h3 className="font-semibold text-gray-800">Guides</h3>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {guides.length > 0 ? (
                    guides.map((guide) => (
                      <button
                        key={guide._id}
                        onClick={() => setSelectedGuide(guide)}
                        className={`w-full p-4 text-left border-b border-gray-100 hover:bg-gray-50 transition-colors ${selectedGuide?._id === guide._id ? "bg-primary-light border-l-4 border-primary" : ""
                          }`}
                      >
                        <h4 className="font-semibold text-gray-800 text-sm">
                          {guide.title}
                        </h4>
                        {guide.estimatedReadTime && (
                          <div className="flex items-center text-xs text-gray-500 mt-2">
                            <FaClock className="mr-1" />
                            {guide.estimatedReadTime} min read
                          </div>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      No guides available
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Guide Content */}
            <div className="lg:col-span-2">
              {selectedGuide ? (
                <div className="bg-white rounded-lg shadow-sm p-5 md:p-8">
                  <div className="mb-4 md:mb-6">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                      {selectedGuide.title}
                    </h2>
                    <p className="text-gray-600 mb-4">
                      {selectedGuide.description}
                    </p>
                    <div className="flex items-center text-sm text-gray-500">
                      <FaClock className="mr-2" />
                      Estimated read time: {selectedGuide.estimatedReadTime || 5} minutes
                    </div>
                  </div>

                  {/* Main Content */}
                  {selectedGuide.content && (
                    <div className="mb-4 pb-4 md:mb-8 md:pb-8 border-b border-gray-200">
                      <div
                        className="prose max-w-none text-gray-700"
                        dangerouslySetInnerHTML={{ __html: selectedGuide.content }}
                      />
                    </div>
                  )}

                  {/* Steps */}
                  {selectedGuide.steps && selectedGuide.steps.length > 0 && (
                    <div className="mb-4 md:mb-8">
                      <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 md:mb-6">
                        Steps to Follow
                      </h3>
                      <div className="space-y-4">
                        {selectedGuide.steps.map((step, index) => (
                          <div key={index} className="flex gap-4">
                            <div className="flex-shrink-0">
                              <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary text-white font-semibold">
                                {index + 1}
                              </div>
                            </div>
                            <div className="flex-1">
                              {step.title && (
                                <h4 className="font-semibold text-gray-800 mb-1">
                                  {step.title}
                                </h4>
                              )}
                              {step.description && (
                                <p className="text-gray-600">
                                  {step.description}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Category Badge */}
                  {selectedGuide.category && (
                    <div className="pt-6 border-t border-gray-200">
                      <span className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full capitalize font-medium">
                        {selectedGuide.category}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm p-5 md:p-8 text-center">
                  <FaBook className="text-4xl text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">Select a guide to view details</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Related Links */}
        <div className="mt-6 pt-4 md:mt-12 md:pt-8 border-t border-gray-200 text-center">
          <div className="inline-flex space-x-4">
            <Link
              to="/faqs"
              className="text-primary hover:text-primary-dark underline"
            >
              FAQs
            </Link>
            <Link
              to="/support-details"
              className="text-primary hover:text-primary-dark underline"
            >
              Support
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowToUse;
